import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const PAGE_SIZE = 6;
const INITIAL_FORM_STATE = {
    id: null,
    title: "",
    intro: "",
    full_description: "",
    author: "",
    date: "",
    imageFile: null,
    imagePreview: "",
    slug: "",
};

function BlogManager() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalBlogs, setTotalBlogs] = useState(0);
    const [form, setForm] = useState(INITIAL_FORM_STATE);

    const previewUrlRef = useRef("");
    const token = localStorage.getItem("token");
    // At the top of your component with other refs
    const quillRef = useRef(null);

    // ================= FETCH BLOGS =================
    const fetchBlogs = async (currentPage = page) => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/blogs?page=${currentPage}&limit=${PAGE_SIZE}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setBlogs(data.data || []);
            setTotalPages(data.pagination?.totalPages || 1);
            setTotalBlogs(data.pagination?.total || 0);
            setPage(data.pagination?.page || 1);
        } catch (err) {
            toast.error("Failed to load blogs");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs(1);
        return () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        };
    }, []);

    // ================= HELPERS =================
    const generateSlug = (title) =>
        title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "");

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }

        const newPreviewUrl = URL.createObjectURL(file);
        previewUrlRef.current = newPreviewUrl;

        setForm((prev) => ({
            ...prev,
            imageFile: file,
            imagePreview: newPreviewUrl,
        }));
    };

    const resetForm = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = "";
        }
        setForm(INITIAL_FORM_STATE);
    };

    // ================= QUILL CONFIGURATION =================
    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ["link", "image", "table"],
            ['clean'],

        ],
        table: true,
        clipboard: { matchVisual: false }
    }), []);

    const formats = useMemo(() => [
        'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
        'blockquote', 'code-block', 'list', 'indent', 'script', // Only 'list' is needed
        'direction', 'color', 'background', 'align', 'table', 'link', 'image'
    ], []);
    const handleTableAction = (e, action) => {
        e.preventDefault(); // Stop focus from breaking

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        // Direct access to Quill's active table module
        const tableModule = editor.getModule('table');
        if (!tableModule) {
            toast.error("Table module is not initialized.");
            return;
        }

        // Force the editor to focus so it registers the active cell selection
        editor.focus();

        switch (action) {
            case 'insertRow':
                tableModule.insertRowBelow();
                break;
            case 'insertColumn':
                tableModule.insertColumnRight();
                break;
            case 'deleteRow':
                tableModule.deleteRow();
                break;
            case 'deleteColumn':
                tableModule.deleteColumn();
                break;
            default:
                break;
        }
    };
    // ================= CREATE / UPDATE =================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.title || !form.intro || !form.full_description) {
            toast.error("Please fill required fields");
            return;
        }
        const cleanHtml = form.full_description.replace(/&nbsp;/g, " ");
        const formData = new FormData();
        formData.append("title", form.title);
        formData.append("intro", form.intro);
        formData.append("full_description", cleanHtml);
        formData.append("author", form.author);
        formData.append("date", form.date);
        formData.append("slug", generateSlug(form.title));
        if (form.imageFile) formData.append("image", form.imageFile);

        try {
            const url = form.id
                ? `${process.env.REACT_APP_API_URL}/blogs/${form.id}`
                : `${process.env.REACT_APP_API_URL}/blogs`;

            await axios({
                method: form.id ? "put" : "post",
                url,
                data: formData,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success(form.id ? "Blog updated" : "Blog created");
            resetForm();
            fetchBlogs(search ? 1 : page);
        } catch (err) {
            console.error(err);
            toast.error("Operation failed");
        }
    };

    // ================= EDIT =================
    const handleEdit = (blog) => {
        const formattedDate = blog.date
            ? new Date(blog.date).toISOString().split("T")[0]
            : "";

        setForm({
            id: blog.id,
            title: blog.title || "",
            intro: blog.intro || "",
            full_description: blog.full_description || "",
            author: blog.author || "",
            date: formattedDate,
            imageFile: null,
            imagePreview: blog.image || "",
            slug: blog.slug || "",
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ================= DELETE =================
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this blog?")) return;

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/blogs/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success("Blog deleted");

            if (blogs.length === 1 && page > 1) {
                fetchBlogs(page - 1);
            } else {
                fetchBlogs(page);
            }
        } catch (err) {
            console.error(err);
            toast.error("Delete failed");
        }
    };

    // ================= SEARCH & PAGINATION LOGIC =================
    const filteredBlogs = useMemo(() => {
        return blogs.filter((b) =>
            (b?.title || '').toLowerCase().includes(search.toLowerCase())
        );
    }, [blogs, search]);

    const paginatedBlogs = useMemo(() => {
        if (search) {
            const start = (page - 1) * PAGE_SIZE;
            return filteredBlogs.slice(start, start + PAGE_SIZE);
        }
        return filteredBlogs;
    }, [filteredBlogs, page, search]);

    const displayedTotalPages = search
        ? Math.ceil(filteredBlogs.length / PAGE_SIZE)
        : totalPages;

    useEffect(() => {
        setPage(1);
    }, [search]);

    const handlePageChange = (newPage) => {
        if (search) {
            setPage(newPage);
        } else {
            fetchBlogs(newPage);
        }
    };

    return (
        <div className="container py-4">
            <ToastContainer position="bottom-right" />
            <h2 className="mb-4">Blog Manager</h2>

            {/* FORM */}
            <form onSubmit={handleSubmit} className="card p-4 mb-5 shadow-sm">
                <h5 className="mb-3">{form.id ? "Edit Blog" : "Add New Blog"}</h5>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label fw-bold">Title</label>
                        <input
                            className="form-control"
                            placeholder="Title"
                            value={form.title}
                            onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Author</label>
                        <input
                            className="form-control"
                            placeholder="Author"
                            value={form.author}
                            onChange={(e) => setForm(p => ({ ...p, author: e.target.value }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={form.date}
                            onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))}
                        />
                    </div>

                    <div className="col-12 my-3">
                        <label className="form-label fw-bold text-primary">Introductory Summary (Short Description)</label>
                        <textarea
                            className="form-control border-primary-subtle"
                            placeholder="Write a catchy 2-3 sentence introduction summary here..."
                            rows={4}
                            style={{ minHeight: "100px", resize: "vertical" }}
                            value={form.intro}
                            onChange={(e) => setForm(p => ({ ...p, intro: e.target.value }))}
                            required
                        />
                    </div>

                    <div className="col-12 my-2 clearfix" style={{ minHeight: "400px" }}>
                        <label className="form-label fw-bold">Full Description</label>
                        <div className="quill-wrapper" style={{ height: "250px", marginBottom: "50px" }}>
                            <div className="mb-2">
                                <span className="form-label fw-bold d-block mb-1" style={{ fontSize: '14px' }}>
                                    Table Cell Operations (Click inside a table cell first):
                                </span>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertRow')}>
                                        ➕ Add Row Below
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertColumn')}>
                                        ➕ Add Column Right
                                    </button>
                                    <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteRow')}>
                                        ❌ Delete Row
                                    </button>
                                    <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteColumn')}>
                                        ❌ Delete Col
                                    </button>
                                </div>
                            </div>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={form.full_description}
                                onChange={(value) => setForm(p => ({ ...p, full_description: value }))}
                                modules={modules}
                                formats={formats}
                                style={{ height: "100%" }}
                                placeholder="Write your main rich text blog content here..."
                            />
                        </div>
                        <style>{`
                        .custom-quill-container .ql-editor a {
                        color: #4f46e5 !important; /* Indigo text */
                        }
                        .ql-editor strong[style*="color"] a,
                        .ql-editor span[style*="color"] a {
                        color: inherit !important;
                        }
                        .ql-container .ql-editor *[style*="color"] a {
                        color: inherit !important;
                        }

                        /* Specific fallback for your exact HTML structure */
                        .ql-container .ql-editor strong a,
                        .ql-container .ql-editor span a {
                        color: inherit !important;
                        }
                        `}</style>
                    </div>

                    <div className="col-md-6 mt-3">
                        <label className="form-label fw-bold">Featured Image</label>
                        <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                    </div>
                    {form.imagePreview && (
                        <div className="col-md-6 d-flex align-items-end mt-3">
                            <img
                                src={form.imagePreview}
                                alt="preview"
                                style={{ height: 80, objectFit: "cover", borderRadius: 4 }}
                            />
                        </div>
                    )}
                </div>
                <div className="d-flex gap-2 mt-4">
                    <button type="submit" className="btn btn-primary">
                        {form.id ? "Update Blog" : "Create Blog"}
                    </button>
                    {form.id && (
                        <button type="button" className="btn btn-secondary" onClick={resetForm}>
                            Cancel Edit
                        </button>
                    )}
                </div>
            </form>

            {/* SEARCH */}
            <div className="mb-3">
                <input
                    className="form-control"
                    placeholder="Search current page blogs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* STATS */}
            <div className="mb-3 text-muted">
                {!search && totalBlogs > 0 && (
                    <small>Showing {blogs.length} of {totalBlogs} blogs (Page {page} of {totalPages})</small>
                )}
                {search && filteredBlogs.length > 0 && (
                    <small>Found {filteredBlogs.length} matching blogs on this page</small>
                )}
            </div>

            {/* TABLE */}
            {loading ? <p>Loading...</p> : (
                <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Image</th>
                                <th>Title</th>
                                <th>Author</th>
                                <th>Date</th>
                                <th>Slug</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedBlogs.map((blog) => (
                                <tr key={blog.id}>
                                    <td width={80}>
                                        {blog.image && (
                                            <img
                                                src={blog.image}
                                                alt=""
                                                style={{ height: 50, width: 50, objectFit: "cover" }}
                                            />
                                        )}
                                    </td>
                                    <td>{blog.title}</td>
                                    <td>{blog.author}</td>
                                    <td>{blog.date ? new Date(blog.date).toLocaleDateString() : '-'}</td>
                                    <td><code style={{ fontSize: 12 }}>{blog.slug}</code></td>
                                    <td width={140}>
                                        <button className="btn btn-sm btn-warning me-2" onClick={() => handleEdit(blog)}>Edit</button>
                                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(blog.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {paginatedBlogs.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">No blogs found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* PAGINATION */}
            {displayedTotalPages > 1 && (
                <div className="d-flex gap-2 mt-3 justify-content-center">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
                    {Array.from({ length: displayedTotalPages }, (_, i) => (
                        <button
                            key={i + 1}
                            className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(page + 1)} disabled={page === displayedTotalPages}>Next</button>
                </div>
            )}
        </div>
    );
}

export default BlogManager;